import { HttpHeaders } from "@angular/common/http";
import { CrudApiOptions, GenResp } from "@ez-library/services/crud-api.service";

/**
 * Headers for background calls that manage their own success/error UX: skip the global
 * loading + error interceptors and send credentialed JSON. Shared so individual services
 * don't each redeclare the same header bag.
 */
export const silentRequestHeaders = new HttpHeaders({
    "Content-Type": "application/json",
    Accept: "application/json, text/plain, */*",
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
});

/**
 * Shared {@link CrudApiService} options for background calls: no retries, no toast feedback
 * (the caller maps errors itself), credentialed JSON via {@link silentRequestHeaders}.
 *
 * These all deliberately differ from {@link defaultCrudApiOptions} (which retries and shows
 * toasts); factoring them here keeps the override in one place instead of every service.
 */
export const silentCrudApiOptions: Partial<CrudApiOptions<"body", "json">> = {
    retries: 0,
    feedbackOptions: { successMode: "none", errorMode: "none" },
    getOptions: { responseType: "json", headers: silentRequestHeaders, withCredentials: true },
    postOptions: { responseType: "json", headers: silentRequestHeaders, withCredentials: true },
};

/**
 * Unwrap a possible {@link GenResp} envelope. CrudApiService is typed to return a wrapper,
 * but the endpoints we call return the DTO directly; this tolerates both shapes so callers
 * don't each carry their own copy of the check.
 */
export function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
    if (response && typeof response === "object" && "data" in response) {
        return (response as GenResp<T>).data;
    }

    return response as T;
}
